const SVG = "http://www.w3.org/2000/svg";

/** A spotlight and pointing hand above the same live board used by normal play. */
export class TutorialView {
  constructor({ root = document, onClose, onNext }) {
    this.root = root;
    this.host = root.querySelector("#tutorial-overlay");
    this.card = root.querySelector("#tutorial-card");
    this.close = root.querySelector("#tutorial-close");
    this.next = root.querySelector("#tutorial-next");
    this.hand = root.querySelector("#tutorial-hand");
    this.abort = new AbortController();
    const options = { signal: this.abort.signal };
    this.close.addEventListener("click", onClose, options);
    this.next.addEventListener("click", onNext, options);
    window.addEventListener("resize", () => this.schedulePosition(), options);
    window.addEventListener("scroll", () => this.schedulePosition(), { ...options, passive: true });
    document.addEventListener("keydown", (event) => {
      if (!this.model || this.hostPaused) return;
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopImmediatePropagation();
        onClose();
      } else if (event.key === "Tab") {
        event.preventDefault();
        const targets = this.focusTargets();
        const current = targets.indexOf(document.activeElement);
        const direction = event.shiftKey ? -1 : 1;
        targets[(current + direction + targets.length) % targets.length].focus({ preventScroll: true });
      }
    }, { ...options, capture: true });
    document.addEventListener("focusin", (event) => {
      if (this.model && !this.hostPaused && !this.focusTargets().includes(event.target))
        this.focusTargets()[0]?.focus({ preventScroll: true });
    }, options);
    this.resizeObserver = new ResizeObserver(() => this.schedulePosition());
    this.resizeObserver.observe(root.querySelector("#board"));
    this.resizeObserver.observe(this.host);
  }
  cells() {
    return [...this.root.querySelectorAll("#cell-grid .cell")];
  }
  setHostPaused(paused) {
    this.hostPaused = paused;
    if (paused) cancelAnimationFrame(this.frame);
    else if (this.model) {
      this.focusTargets()[0]?.focus({ preventScroll: true });
      this.schedulePosition();
    }
  }
  focusTargets() {
    return [
      this.close,
      ...this.cells().filter((cell) => this.model?.targets.includes(Number(cell.dataset.index))),
      ...(!this.next.hidden ? [this.next] : []),
    ];
  }
  render(model) {
    const previous = this.model;
    this.model = model;
    this.host.hidden = !model;
    this.root.querySelector(".game-shell").classList.toggle("learning", Boolean(model));
    if (!model) {
      if (previous) {
        const canRestore = this.previousFocus?.isConnected &&
          !this.previousFocus.disabled &&
          this.previousFocus.matches("button, input, [tabindex]");
        const target = canRestore ? this.previousFocus : this.cells()[0];
        target?.focus({ preventScroll: true });
      }
      return;
    }
    if (!previous) this.previousFocus = document.activeElement;
    this.root.querySelector("#tutorial-title").textContent = model.title;
    const copy = this.root.querySelector("#tutorial-copy");
    if (copy.textContent !== model.text) {
      copy.replaceChildren(...model.text.split(/(double-(?:tap|click))/gi).map((text) => {
        if (!/^double-(?:tap|click)$/i.test(text)) return document.createTextNode(text);
        const emphasis = document.createElement("strong");
        emphasis.className = "tutorial-gesture";
        emphasis.textContent = text;
        return emphasis;
      }));
    }
    this.root.querySelector("#tutorial-progress").textContent = `${model.index + 1} / ${model.total}`;
    this.next.hidden = !["continue", "finish"].includes(model.action);
    this.next.textContent = model.button || "Got it";
    this.hand.hidden = model.hand === null;
    this.hand.classList.toggle("double-tap", model.action === "double");
    this.cells().forEach((cell) => {
      cell.tabIndex = model.targets.includes(Number(cell.dataset.index)) ? 0 : -1;
    });
    if (!this.hostPaused && (!previous || previous.index !== model.index || !this.focusTargets().includes(document.activeElement))) {
      const target = model.targets.length
        ? this.cells()[model.targets[0]]
        : this.next;
      target.focus({ preventScroll: true });
    }
    this.position();
    this.schedulePosition();
  }
  schedulePosition() {
    cancelAnimationFrame(this.frame);
    if (this.model && !this.hostPaused) this.frame = requestAnimationFrame(() => this.position());
  }
  position() {
    if (!this.model) return;
    const shade = this.root.querySelector("#tutorial-shade");
    // A reserved scrollbar narrows the SVG without changing window.innerWidth.
    const { width, height } = shade.getBoundingClientRect();
    shade.setAttribute("viewBox", `0 0 ${width} ${height}`);
    const board = this.root.querySelector("#board").getBoundingClientRect();
    const sidebar = width >= 560 && board.left >= 220;
    const cardWidth = sidebar ? Math.min(320, board.left - 30) : Math.min(370, width - 28);
    this.card.style.width = `${cardWidth}px`;
    this.card.style.maxHeight = `${sidebar ? height - 24 : Math.max(100, board.top - 26)}px`;
    this.card.classList.remove("compact", "tight");
    if (this.card.scrollHeight > this.card.clientHeight + 1)
      this.card.classList.add("compact");
    if (this.card.scrollHeight > this.card.clientHeight + 1)
      this.card.classList.add("tight");
    const left = sidebar
      ? Math.max(12, (board.left - cardWidth) / 2)
      : (width - cardWidth) / 2;
    const top = sidebar
      ? Math.max(12, (height - this.card.offsetHeight) / 2)
      : Math.max(12, board.top - this.card.offsetHeight - 14);
    this.card.style.left = `${left}px`;
    this.card.style.top = `${top}px`;
    const card = this.card.getBoundingClientRect();
    this.close.style.left = `${card.right - 44}px`;
    this.close.style.top = `${card.top + 8}px`;
    const cells = this.cells();
    const cutouts = this.model.focus.map((index) => {
      const bounds = cells[index].getBoundingClientRect();
      const rect = document.createElementNS(SVG, "rect");
      const attributes = {
        x: bounds.left - 2, y: bounds.top - 2,
        width: bounds.width + 4, height: bounds.height + 4,
        rx: 7, fill: "black",
      };
      for (const [name, value] of Object.entries(attributes))
        rect.setAttribute(name, value);
      return rect;
    });
    this.root.querySelector("#tutorial-cutouts").replaceChildren(...cutouts);
    if (this.model.hand !== null) {
      const bounds = cells[this.model.hand].getBoundingClientRect();
      const x = bounds.left + bounds.width * 0.58;
      const y = bounds.top + bounds.height * 0.54;
      this.hand.style.left = `${x - 27}px`;
      this.hand.style.top = `${y - 7}px`;
      this.hand.style.setProperty("--hand-angle", y + 112 > height - 12 ? "180deg" : "0deg");
    }
  }
  destroy() {
    this.abort.abort();
    this.resizeObserver.disconnect();
    cancelAnimationFrame(this.frame);
  }
}
